import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { STAFF_ROLE_GROUPS, hasRole } from '../utils/roles';
import { authAPI } from '../services/api';

const STAFF_ADMIN_LOGIN_URL = (() => {
  const raw = (process.env.REACT_APP_ADMIN_LOGIN_URL || '/admin/login/').trim();
  return raw;
})();

// برچسب ساده‌ی نقش برای نمایش در پیام‌های دسترسی (نسخه‌ی سبک AdminPanel)
// P3 (بند ۴.۶): اولویت با گروه‌های نقش (`roles[]`) است و `user_type` فقط
// fallback — قبلاً فقط user_type خوانده می‌شد، یعنی پیک/پیکی (که user_type
// شان عمداً customer است) در پیام دسترسی «مشتری» نوشته می‌شد.
const roleLabel = (u) => {
  if (!u) return 'کاربر';
  if (hasRole(u, 'admin')) return 'مدیر';
  if (hasRole(u, 'support')) return 'پشتیبانی';
  if (hasRole(u, 'seller')) return 'فروشنده';
  if (hasRole(u, 'pike')) return 'پیک';
  if (hasRole(u, 'courier')) return 'کوریر';
  if (hasRole(u, 'customer')) return 'مشتری';
  return u?.name || u?.username || 'کاربر';
};

const StaffRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user, login } = useAuth();
  const [bridgeLoading, setBridgeLoading] = useState(false);
  const triedRef = useRef(false);

  // اگر بدون توکن وارد /staff شد (مثلاً از نوار ادمین)، پلِ هویت را دوباره بزنیم.
  useEffect(() => {
    if (triedRef.current || isAuthenticated || loading) return;
    triedRef.current = true;
    let cancelled = false;
    setBridgeLoading(true);
    (async () => {
      try {
        const res = await authAPI.getSessionUser();
        const d = res && res.data;
        if (d && d.token && d.user && !cancelled) {
          login(d.user, d.token, d.refresh);
        }
      } catch (_) {}
      if (!cancelled) {
        setBridgeLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, loading, login]);

  if (loading || bridgeLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kadolar-600"></div>
      </div>
    );
  }

  // کاربر staff (از پلِ هویت / از ادمین) باید JWT داشته باشد؛ اگر نبود، به login ادمین بفرست
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow p-8 text-center max-w-md w-full">
          <h1 className="text-2xl font-bold mb-4">دسترسی فقط برای پرسنل</h1>
          <p className="text-gray-600 mb-6">
            برای دسترسی به پنل کاربری، ابتدا در <strong>پنل ادمین کادولار</strong> با حساب کاربری خود وارد شوید.
          </p>
          <a
            href={STAFF_ADMIN_LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center px-6 py-4 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 transition-colors"
          >
            ورود به پنل ادمین ↪
          </a>
          <Link
            to="/"
            className="block mt-4 text-sm text-gray-500 hover:text-gray-700"
          >
            بازگشت به فروشگاه
          </Link>
        </div>
      </div>
    );
  }

  const isSuperuser = user?.is_superuser === true;
  // P3 (بند ۴.۶): تشخیص نقش از utils/roles (نسخهٔ محلی extractRoles/hasRole حذف شد)
  const hasUserRole = (name) => hasRole(user, name);
  const isStaffLike = STAFF_ROLE_GROUPS.some((r) => hasUserRole(r));
  const roleAllowed = Array.isArray(allowedRoles)
    ? allowedRoles.some((r) => hasUserRole(r))
    : true;

  // P1 (بند ۲.۴): فروشنده «پرسنل» نیست (STAFF_ROLE_GROUPS او را ندارد) ولی
  // مسیر تسویه صریحاً برای او باز شده است. اگر خودِ route نقش کاربر را نام
  // برده، دیوارِ «فقط پرسنل» رد می‌شود؛ گارد واقعی داده‌ها سمت API است.
  const routeListsMyRole = Array.isArray(allowedRoles) && roleAllowed;

  if (!(isStaffLike || isSuperuser || routeListsMyRole)) {
    // کاربر لاگین‌شده با OTP (مشتری/فروشنده) که مستقیماً /staff را تایپ کرد
    // → پیام دسترسی (نه redirect خام به /)
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow p-8 text-center max-w-md w-full">
          <h1 className="text-2xl font-bold mb-4">دسترسی فقط برای پرسنل</h1>
          <p className="text-gray-600 mb-6">
            این بخش فقط برای پرسنل کادولار در دسترس است.
            حساب شما ({roleLabel(user)}) به پنل کاربری دسترسی ندارد.
          </p>
          <Link
            to="/"
            className="block w-full text-center px-6 py-4 bg-kadolar-600 text-white font-bold rounded-2xl hover:bg-kadolar-700 transition-colors"
          >
            بازگشت به فروشگاه
          </Link>
        </div>
      </div>
    );
  }

  if (Array.isArray(allowedRoles) && !roleAllowed && !isSuperuser) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow p-8 text-center max-w-md w-full">
          <h1 className="text-2xl font-bold mb-4">دسترسی محدود</h1>
          <p className="text-gray-600 mb-6">
            نقش شما برای این بخش مجاز نیست.
          </p>
          <Link
            to="/"
            className="block w-full text-center px-6 py-4 bg-kadolar-600 text-white font-bold rounded-2xl hover:bg-kadolar-700 transition-colors"
          >
            بازگشت به فروشگاه
          </Link>
        </div>
      </div>
    );
  }

  return children;
};

export default StaffRoute;
